import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../pages/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFileInvoice,
    faCamera,
    faUser,
    faStore,
    faSave,
    faTimes,
    faSpinner,
    faRefresh,
    faTag,
    faImage,
    faUpload,
    faChevronLeft,
    faChevronRight,
    faPlus,
    faTimesCircle,
    faExclamationTriangle,
    faCheckCircle,
    faTrashAlt,
    faEdit,
    faSearch,
    faEye,
    faExpand
} from '@fortawesome/free-solid-svg-icons';
import "../module_mechanics/Styles/Form.css";

// ==============================================
// API Endpoint
// ==============================================
const API_BASE = 'https://ourzestapp.online/oz_module/solar_module';
const INVOICE_API = `${API_BASE}/solar_pinvoices.php`;

// ==============================================
// Translation Utility
// ==============================================
const createInvoiceTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_invoice_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Invoice Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_invoice_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        const dict = {
            'ur': {
                'New Invoice': 'نیا انوائس',
                'Edit Invoice': 'انوائس میں ترمیم کریں',
                'Invoice Number': 'انوائس نمبر',
                'Customer': 'کسٹمر',
                'Bill Number': 'بل نمبر',
                'Bill Pictures': 'بل کی تصاویر',
                'Save Invoice': 'انوائس محفوظ کریں',
                'Update Invoice': 'انوائس اپ ڈیٹ کریں',
                'Clear': 'صاف کریں',
                'Loading': 'لوڈ ہو رہا ہے...',
                'Saving': 'محفوظ ہو رہا ہے...',
                'Invoice Saved': 'انوائس کامیابی سے محفوظ ہو گئی!',
                'Invoice Updated': 'انوائس کامیابی سے اپ ڈیٹ ہو گئی!',
                'Failed to save': 'محفوظ کرنے میں ناکامی',
                'Failed to update': 'اپ ڈیٹ کرنے میں ناکامی',
                'Network Error': 'نیٹ ورک کی خرابی',
                'Success': 'کامیابی',
                'Error': 'خرابی',
                'Warning': 'انتباہ',
                'Delete': 'حذف کریں',
                'Edit': 'ترمیم کریں',
                'Refresh': 'ریفریش کریں',
                'Upload Bill': 'بل اپ لوڈ کریں',
                'Scan Bill': 'بل اسکین کریں',
                'Capture Bill': 'بل کی تصویر لیں',
                'Scanning': 'اسکین ہو رہا ہے...',
                'Camera Ready': 'کیمرہ تیار ہے',
                'Image too large': 'تصویر بہت بڑی ہے',
                'Processing image': 'تصویر پروسیس ہو رہی ہے',
                'Auto-generating number': 'نمبر خودکار بن رہا ہے',
                'Add another picture': 'مزید تصویر شامل کریں',
                'Maximum 10 pictures': 'زیادہ سے زیادہ 10 تصاویر',
                'View pictures': 'تصاویر دیکھیں'
            },
            'ar': {
                'New Invoice': 'فاتورة جديدة',
                'Edit Invoice': 'تعديل الفاتورة',
                'Invoice Number': 'رقم الفاتورة',
                'Customer': 'العميل',
                'Bill Number': 'رقم الفاتورة',
                'Bill Pictures': 'صور الفاتورة',
                'Save Invoice': 'حفظ الفاتورة',
                'Update Invoice': 'تحديث الفاتورة',
                'Clear': 'مسح',
                'Loading': 'جاري التحميل...',
                'Saving': 'جاري الحفظ...',
                'Invoice Saved': 'تم حفظ الفاتورة بنجاح!',
                'Invoice Updated': 'تم تحديث الفاتورة بنجاح!',
                'Failed to save': 'فشل في الحفظ',
                'Failed to update': 'فشل في التحديث',
                'Network Error': 'خطأ في الشبكة',
                'Success': 'نجاح',
                'Error': 'خطأ',
                'Warning': 'تحذير',
                'Delete': 'حذف',
                'Edit': 'تعديل',
                'Refresh': 'تحديث',
                'Upload Bill': 'رفع الفاتورة',
                'Scan Bill': 'مسح الفاتورة',
                'Capture Bill': 'التقاط الفاتورة',
                'Scanning': 'جاري المسح...',
                'Camera Ready': 'الكاميرا جاهزة',
                'Image too large': 'الصورة كبيرة جداً',
                'Processing image': 'جاري معالجة الصورة',
                'Auto-generating number': 'جاري إنشاء الرقم تلقائياً',
                'Add another picture': 'إضافة صورة أخرى',
                'Maximum 10 pictures': 'الحد الأقصى 10 صور',
                'View pictures': 'عرض الصور'
            }
        };

        if (dict[targetLang] && dict[targetLang][text]) {
            return dict[targetLang][text];
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        newInvoice: 'New Invoice',
        editInvoice: 'Edit Invoice',
        invoiceNumber: 'Invoice Number',
        customer: 'Customer',
        billNumber: 'Bill Number',
        billPictures: 'Bill Pictures',
        saveInvoice: 'Save Invoice',
        updateInvoice: 'Update Invoice',
        clear: 'Clear',
        loading: 'Loading...',
        saving: 'Saving...',
        invoiceSaved: 'Invoice saved successfully!',
        invoiceUpdated: 'Invoice updated successfully!',
        failedSave: 'Failed to save invoice',
        failedUpdate: 'Failed to update invoice',
        networkError: 'Network error occurred',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        delete: 'Delete',
        edit: 'Edit',
        refresh: 'Refresh',
        uploadBill: 'Upload Bill',
        scanBill: 'Scan Bill',
        captureBill: 'Capture Bill',
        scanning: 'Scanning...',
        cameraReady: 'Camera Ready',
        imageTooLarge: 'Image too large',
        processingImage: 'Processing image...',
        autoGenerating: 'Auto-generating number...',
        addAnotherPicture: 'Add another picture',
        maxPictures: 'Maximum 10 pictures',
        viewPictures: 'View pictures'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const invoiceTranslator = createInvoiceTranslator();

// ==============================================
// Toast Notification Component
// ==============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations?.success || 'Success';
            case 'error': return translations?.error || 'Error';
            case 'warning': return translations?.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==============================================
// Bill Pictures Gallery Component
// ==============================================
const BillPicturesGallery = ({ pictures, onRemove, onAdd, maxPictures = 10, translations }) => {
    const [selectedIndex, setSelectedIndex] = useState(null);
    const fileInputRef = useRef(null);

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            alert(translations?.imageTooLarge || 'Image too large. Please select an image under 5MB.');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const imageUrl = e.target.result;
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = Math.min(img.width, 1024);
                canvas.height = Math.min(img.height, 1024);
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                const processedImage = canvas.toDataURL('image/jpeg', 0.8);
                onAdd(processedImage);
            };
            img.src = imageUrl;
        };
        reader.readAsDataURL(file);
        event.target.value = '';
    };

    const handleCameraCapture = async () => {
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'environment',
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                },
                audio: false
            });

            const video = document.createElement('video');
            video.srcObject = mediaStream;
            await video.play();

            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth || 1280;
            canvas.height = video.videoHeight || 720;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            const imageUrl = canvas.toDataURL('image/jpeg', 0.8);
            
            // Resize
            const img = new Image();
            img.onload = () => {
                const resizedCanvas = document.createElement('canvas');
                resizedCanvas.width = Math.min(img.width, 1024);
                resizedCanvas.height = Math.min(img.height, 1024);
                const resizedCtx = resizedCanvas.getContext('2d');
                resizedCtx.drawImage(img, 0, 0, resizedCanvas.width, resizedCanvas.height);
                onAdd(resizedCanvas.toDataURL('image/jpeg', 0.8));
            };
            img.src = imageUrl;

            mediaStream.getTracks().forEach(track => track.stop());
        } catch (error) {
            console.error('Error capturing image:', error);
            if (fileInputRef.current) {
                fileInputRef.current.click();
            }
        }
    };

    const openImageViewer = (index) => {
        setSelectedIndex(index);
        document.body.style.overflow = 'hidden';
    };

    const closeImageViewer = () => {
        setSelectedIndex(null);
        document.body.style.overflow = '';
    };

    const navigateImage = (direction) => {
        if (selectedIndex === null) return;
        const newIndex = selectedIndex + direction;
        if (newIndex >= 0 && newIndex < pictures.length) {
            setSelectedIndex(newIndex);
        }
    };

    return (
        <div className="ozui-bill-gallery">
            <div className="ozui-gallery-grid">
                {pictures.map((pic, index) => (
                    <div key={index} className="ozui-gallery-item">
                        <img 
                            src={pic} 
                            alt={`Bill ${index + 1}`} 
                            onClick={() => openImageViewer(index)}
                        />
                        <button 
                            className="ozui-gallery-remove"
                            onClick={() => onRemove(index)}
                            type="button"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                ))}
                
                {pictures.length < maxPictures && (
                    <div className="ozui-gallery-add">
                        <input 
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileUpload}
                            style={{ display: 'none' }}
                        />
                        <button 
                            type="button"
                            className="ozui-gallery-add-btn"
                            onClick={handleCameraCapture}
                        >
                            <FontAwesomeIcon icon={faCamera} />
                            <span>{translations?.captureBill || 'Capture'}</span>
                        </button>
                        <button 
                            type="button"
                            className="ozui-gallery-add-btn ozui-gallery-upload-btn"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <FontAwesomeIcon icon={faUpload} />
                            <span>{translations?.uploadBill || 'Upload'}</span>
                        </button>
                    </div>
                )}
            </div>

            {pictures.length > 0 && (
                <div className="ozui-gallery-info">
                    <span>{pictures.length} / {maxPictures} {translations?.billPictures || 'pictures'}</span>
                </div>
            )}

            {/* Image Viewer Overlay */}
            {selectedIndex !== null && pictures[selectedIndex] && (
                <div className="ozui-image-viewer-overlay" onClick={closeImageViewer}>
                    <div className="ozui-image-viewer-content" onClick={(e) => e.stopPropagation()}>
                        <button className="ozui-image-viewer-close" onClick={closeImageViewer}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                        
                        <div className="ozui-image-viewer-nav">
                            <button 
                                className="ozui-image-viewer-nav-btn"
                                onClick={() => navigateImage(-1)}
                                disabled={selectedIndex === 0}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                            </button>
                            <img src={pictures[selectedIndex]} alt={`Bill ${selectedIndex + 1}`} />
                            <button 
                                className="ozui-image-viewer-nav-btn"
                                onClick={() => navigateImage(1)}
                                disabled={selectedIndex === pictures.length - 1}
                            >
                                <FontAwesomeIcon icon={faChevronRight} />
                            </button>
                        </div>
                        
                        <div className="ozui-image-viewer-info">
                            {selectedIndex + 1} / {pictures.length}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ==============================================
// Main Pending Invoices Component
// ==============================================
const PendingInvoices = ({ invoiceId = null, onSuccess = null }) => {
    const { user, currentShop } = useAuth();

    // ONLY ESSENTIAL FIELDS
    const initialFormData = {
        invoice_number: '',
        customer_name: '',
        bill_number: '',
        bill_pictures: [],
        username: '',
        shop_id: null
    };

    const [formData, setFormData] = useState(initialFormData);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [errors, setErrors] = useState({});
    const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
    const [translations, setTranslations] = useState({});
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ourzest_darkMode') === 'true');
    const [currentThemeColor, setCurrentThemeColor] = useState(() => localStorage.getItem('ourzest_theme') || '#6366f1');
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [isLoading, setIsLoading] = useState(false);

    // Refs
    const toastTimerRef = useRef(null);
    const isSavingRef = useRef(false);
    const invoiceNumberRef = useRef(null);

    // ==============================================
    // Toast Notification
    // ==============================================
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        };
    }, []);

    // ==============================================
    // Auto-generate Invoice Number
    // ==============================================
    const generateInvoiceNumber = useCallback(async () => {
        if (!currentShop?.shop_id) return;

        try {
            const response = await fetch(INVOICE_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_next_invoice_number',
                    shop_id: currentShop.shop_id,
                    username: user?.username
                })
            });
            const data = await response.json();
            if (data.status === 'success' && data.data) {
                setFormData(prev => ({ 
                    ...prev, 
                    invoice_number: data.data.invoice_number 
                }));
            }
        } catch (error) {
            console.error('Error generating invoice number:', error);
            const timestamp = Date.now().toString().slice(-6);
            const shopPrefix = currentShop.shop_id.toString().slice(-3);
            setFormData(prev => ({ 
                ...prev, 
                invoice_number: `INV-${shopPrefix}-${timestamp}` 
            }));
        }
    }, [currentShop?.shop_id, user?.username]);

    // ==============================================
    // Generate invoice number on mount
    // ==============================================
    useEffect(() => {
        if (currentShop?.shop_id && !isEditMode) {
            generateInvoiceNumber();
        }
    }, [currentShop?.shop_id, isEditMode, generateInvoiceNumber]);

    // ==============================================
    // Load invoice data for edit
    // ==============================================
    useEffect(() => {
        if (invoiceId) {
            setIsEditMode(true);
            loadInvoiceData(invoiceId);
        }
    }, [invoiceId]);

    const loadInvoiceData = async (id) => {
        if (!currentShop?.shop_id || !user?.username) return;

        setIsLoading(true);
        try {
            const response = await fetch(INVOICE_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_record',
                    id: id,
                    shop_id: currentShop.shop_id,
                    username: user.username
                })
            });
            const data = await response.json();
            if (data.status === 'success' && data.data) {
                const invoice = data.data;
                let pictures = [];
                try {
                    if (invoice.bill_pictures) {
                        pictures = JSON.parse(invoice.bill_pictures);
                    } else if (invoice.bill_picture) {
                        pictures = [invoice.bill_picture];
                    }
                } catch (e) {
                    pictures = [];
                }
                
                setFormData({
                    invoice_number: invoice.invoice_number || '',
                    customer_name: invoice.customer_name || '',
                    bill_number: invoice.bill_number || '',
                    bill_pictures: pictures || [],
                    username: invoice.username || user.username,
                    shop_id: invoice.shop_id || currentShop.shop_id
                });
            } else {
                showToast('error', data.message || 'Failed to load invoice');
            }
        } catch (error) {
            console.error('Error loading invoice:', error);
            showToast('error', translations?.networkError || 'Network error');
        } finally {
            setIsLoading(false);
        }
    };

    // ==============================================
    // Theme Color Auto-Sync
    // ==============================================
    const applyTheme = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
            color = '#6366f1';
        }
        document.documentElement.style.setProperty('--ozui-primary', color);
        document.documentElement.style.setProperty('--ozui-primary-dark', color);
        document.documentElement.style.setProperty('--ozui-primary-light', color);

        let r, g, b;
        if (color.startsWith('#')) {
            r = parseInt(color.slice(1, 3), 16);
            g = parseInt(color.slice(3, 5), 16);
            b = parseInt(color.slice(5, 7), 16);
        } else {
            r = 99; g = 102; b = 241;
        }

        let style = document.getElementById('dynamic-orb-styles-invoice');
        if (!style) {
            style = document.createElement('style');
            style.id = 'dynamic-orb-styles-invoice';
            document.head.appendChild(style);
        }

        style.textContent = `
            .ozui-header-bg-animation .gradient-orb.orb-1 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.4) 0%, rgba(${r}, ${g}, ${b}, 0.1) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-2 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.35) 0%, rgba(${r}, ${g}, ${b}, 0.08) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-3 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.3) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-4 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.25) 0%, rgba(${r}, ${g}, ${b}, 0.03) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-5 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0.02) 100%);
            }
        `;

        setCurrentThemeColor(color);
    }, []);

    useEffect(() => {
        const savedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
        applyTheme(savedTheme);

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                applyTheme(e.newValue || '#6366f1');
            }
        };

        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
        };
    }, [applyTheme]);

    // ==============================================
    // Dark Mode Auto-Sync
    // ==============================================
    useEffect(() => {
        const syncDarkMode = () => {
            const isDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDarkMode);

            const container = document.querySelector('.ozui-container');
            if (container) {
                if (isDarkMode) {
                    container.classList.add('ozui-dark');
                } else {
                    container.classList.remove('ozui-dark');
                }
            }

            if (isDarkMode) {
                document.body.classList.add('ozui-dark');
                document.documentElement.classList.add('ozui-dark');
            } else {
                document.body.classList.remove('ozui-dark');
                document.documentElement.classList.remove('ozui-dark');
            }
        };

        syncDarkMode();

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        };

        const handleDarkModeToggle = (event) => {
            if (event.detail) {
                const isDark = event.detail.darkMode;
                setDarkMode(isDark);
                const container = document.querySelector('.ozui-container');
                if (container) {
                    if (isDark) {
                        container.classList.add('ozui-dark');
                    } else {
                        container.classList.remove('ozui-dark');
                    }
                }
            } else {
                syncDarkMode();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
        };
    }, []);

    // ==============================================
    // Language Auto-Sync
    // ==============================================
    useEffect(() => {
        const loadTranslations = async (lang) => {
            const trans = await invoiceTranslator.getTranslations(lang);
            setTranslations(trans);
        };

        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };

        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                    loadTranslations(newLang);
                }
            }
        };

        loadTranslations(currentLanguage);

        window.addEventListener('storage', handleLanguageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);

        const interval = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // ==============================================
    // RTL Direction Sync
    // ==============================================
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const container = document.querySelector('.ozui-container');

        if (container) {
            if (isRTL) {
                container.classList.add('ozui-rtl');
                container.classList.remove('ozui-ltr');
            } else {
                container.classList.add('ozui-ltr');
                container.classList.remove('ozui-rtl');
            }
        }

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }
    }, [currentLanguage]);

    // ==============================================
    // Handle Bill Pictures
    // ==============================================
    const handleAddPicture = useCallback((imageDataUrl) => {
        setFormData(prev => {
            if (prev.bill_pictures.length >= 10) {
                showToast('warning', translations?.maxPictures || 'Maximum 10 pictures');
                return prev;
            }
            return {
                ...prev,
                bill_pictures: [...prev.bill_pictures, imageDataUrl]
            };
        });
    }, [showToast, translations]);

    const handleRemovePicture = useCallback((index) => {
        setFormData(prev => ({
            ...prev,
            bill_pictures: prev.bill_pictures.filter((_, i) => i !== index)
        }));
    }, []);

    // ==============================================
    // Handle Form Changes
    // ==============================================
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    // ==============================================
    // Validate Form
    // ==============================================
    const validateForm = () => {
        const newErrors = {};

        if (!formData.invoice_number?.trim()) {
            newErrors.invoice_number = 'Invoice number is required';
        }  

        if (!formData.shop_id) {
            newErrors.shop_id = 'Shop is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ==============================================
    // Handle Submit
    // ==============================================
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSavingRef.current) {
            showToast('warning', 'Please wait, invoice is already being processed...');
            return;
        }

        if (!currentShop?.shop_id) {
            showToast('error', 'No shop selected. Please select a shop first.');
            return;
        }

        if (!validateForm()) {
            showToast('error', 'Please fix the errors in the form');
            return;
        }

        isSavingRef.current = true;
        setSaving(true);

        try {
            const username = user?.username || 'unknown';
            const action = isEditMode ? 'edit' : 'add';
            
            const payload = {
                action: action,
                invoice_number: formData.invoice_number,
                customer_name: formData.customer_name,
                bill_number: formData.bill_number,
                bill_pictures: JSON.stringify(formData.bill_pictures),
                shop_id: currentShop.shop_id,
                username: username
            };

            if (isEditMode && invoiceId) {
                payload.id = invoiceId;
            }

            const response = await fetch(INVOICE_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', 
                    isEditMode ? (translations?.invoiceUpdated || 'Invoice updated successfully!') 
                               : (translations?.invoiceSaved || 'Invoice saved successfully!')
                );

                if (!isEditMode) {
                    setFormData({
                        ...initialFormData,
                        shop_id: currentShop.shop_id,
                        username: username
                    });
                    generateInvoiceNumber();
                }

                if (onSuccess) {
                    onSuccess(data.data?.id || invoiceId);
                }
            } else {
                showToast('error', data.message || (isEditMode ? translations?.failedUpdate : translations?.failedSave) || 'Failed to save invoice');
            }
        } catch (error) {
            console.error('Submit error:', error);
            showToast('error', error.message || translations?.networkError || 'Network error occurred');
        } finally {
            isSavingRef.current = false;
            setSaving(false);
        }
    };

    // ==============================================
    // Handle Clear Form
    // ==============================================
    const handleClear = () => {
        if (isEditMode) {
            setIsEditMode(false);
        }
        setFormData({
            ...initialFormData,
            shop_id: currentShop?.shop_id || null,
            username: user?.username || ''
        });
        setErrors({});
        generateInvoiceNumber();
        showToast('success', 'Form cleared');
        
        setTimeout(() => {
            if (invoiceNumberRef.current) {
                invoiceNumberRef.current.focus();
            }
        }, 100);
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    if (isLoading) {
        return (
            <div className={`ozui-container ${isRTL ? 'ozui-rtl' : 'ozui-ltr'} ${darkMode ? 'ozui-dark' : ''}`}>
                <div className="ozui-loading-state">
                    <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                    <p>{translations?.loading || 'Loading...'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`ozui-container ${isRTL ? 'ozui-rtl' : 'ozui-ltr'} ${darkMode ? 'ozui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            <div className="ozui-sticky-header">
                <div className="ozui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozui-header-content">
                    <div className="ozui-header-left">
                        <div className="ozui-header-title">
                            <h1>
                                {isEditMode ? (translations?.editInvoice || 'Edit Invoice') : (translations?.newInvoice || 'New Invoice')}
                            </h1>
                        </div>
                    </div>

                    <div className="ozui-header-right">
                        <div className="ozui-header-actions">
                            <button
                                className="ozui-action-btn clear-draft"
                                onClick={handleClear}
                                disabled={saving}
                                title={translations?.clear || 'Clear'}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                <span>{translations?.clear || 'Clear'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="ozui-form">
                <div className="ozui-form-row">
                    {/* Invoice Number - Auto-generated */}
                    <div className={`ozui-field ${errors.invoice_number ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                ref={invoiceNumberRef}
                                type="text"
                                name="invoice_number"
                                id="invoice_number"
                                value={formData.invoice_number}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={saving || isEditMode}
                                readOnly={!isEditMode}
                                className={!isEditMode ? 'ozui-auto-generated' : ''}
                            />
                            <label htmlFor="invoice_number">
                                <FontAwesomeIcon icon={faFileInvoice} />
                                {translations?.invoiceNumber || 'Invoice Number'} <span>*</span>
                            </label>
                        </div>
                        {errors.invoice_number && <small>{errors.invoice_number}</small>}
                    </div>
                {/* </div> */}

                {/* <div className="ozui-form-row"> */}
                   
                    {/* Bill Number */}
                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="bill_number"
                                id="bill_number"
                                value={formData.bill_number}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={saving}
                            />
                            <label htmlFor="bill_number">
                                <FontAwesomeIcon icon={faTag} />
                                {translations?.billNumber || 'Bill Number'}
                            </label>
                        </div>
                    </div>
                </div>

                {/* Bill Pictures */}
                <div className="ozui-form-row">
                    <div className="ozui-field ozui-full-width">
                        <div className="ozui-scanner-section">
                            <label className="ozui-scanner-label">
                                <FontAwesomeIcon icon={faImage} />
                                {translations?.billPictures || 'Bill Pictures'}
                                <span className="ozui-picture-count">
                                    ({formData.bill_pictures.length}/10)
                                </span>
                            </label>
                            <BillPicturesGallery
                                pictures={formData.bill_pictures}
                                onRemove={handleRemovePicture}
                                onAdd={handleAddPicture}
                                maxPictures={10}
                                translations={translations}
                            />
                        </div>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="ozui-form-actions">
                    <button
                        type="submit"
                        className="ozui-btn ozui-btn-primary"
                        disabled={saving || isLoading || !currentShop}
                    >
                        {saving ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>{translations?.saving || 'Saving...'}</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSave} />
                                <span>{isEditMode ? (translations?.updateInvoice || 'Update Invoice') : (translations?.saveInvoice || 'Save Invoice')}</span>
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default PendingInvoices;